/* eslint-disable import/no-cycle */
import { BaseEvent, EventListener, Vector3 } from 'three';
import { EventDispatcher, Vector2 } from 'three';

import {
  tiltAngleBetweenIntersections,
  panAngleBetweenIntersections,
} from 'shared/utils/CameraUtils';
import SphereCamera from 'shared/renderingEngine/SphereCamera';
import MomentumAnimation from 'shared/renderingEngine/animations/MomentumAnimation';
import { Axis } from 'shared/interfaces/Enums';
import { arrayAverage } from 'shared/utils/ArrayUtils';
import type CameraAnimation from 'shared/renderingEngine/animations/CameraAnimation';
import RotateAnimation from 'shared/renderingEngine/animations/RotateAnimation';
import camera3DEventManager, { Camera3DEvents } from 'shared/eventManager/Camera3DEventManager';
import heatMapEventManager, { HeatMapEvents } from 'shared/eventManager/HeatmapEventManager';
import { assertDefined } from '../utils/debug';

const MAXIMUM_MOMENTUM_START_SPEED = 0.05;
const MAX_SPEED_ARRAY_LENGTH = 5;

export enum CameraEvent {
  CameraUpdate = 'CameraUpdate',
  ControlsMove = 'ControlsMove',
  ControlsZoom = 'ControlsZoom',
}

type CameraEventType = BaseEvent & {
  type: CameraEvent;
};

export default class SphereCameraControls extends EventDispatcher<CameraEventType> {
  private animationChain: Set<CameraAnimation>;

  private angularSpeeds = { [Axis.X]: [] as number[], [Axis.Y]: [] as number[] };

  private speedSavedAt: number | undefined;

  private heatMapMovementDirection: 'left' | 'right';

  private rotateAnimation: RotateAnimation;

  constructor(public sphereCamera: SphereCamera) {
    super();
    this.animationChain = new Set<CameraAnimation>();
    this.rotateAnimation = new RotateAnimation(this.sphereCamera);
  }

  move(latestIntersect: Vector3, previousIntersect: Vector3) {
    this.dispatchEvent({ type: CameraEvent.ControlsMove });
    const { panAngle } = this.moveBetweenIntersections(latestIntersect, previousIntersect);

    if (panAngle > 0) {
      this.heatMapMovementDirection = 'left';
    } else {
      this.heatMapMovementDirection = 'right';
    }

    camera3DEventManager.trigger(Camera3DEvents.DRAG_CAMERA);
  }

  private moveBetweenIntersections(latestIntersect: Vector3, previousIntersect: Vector3) {
    const tiltAngle = tiltAngleBetweenIntersections(
      latestIntersect,
      previousIntersect,
      this.sphereCamera.camera.position,
    );
    const panAngle = panAngleBetweenIntersections(latestIntersect, previousIntersect);

    this.rotateCameras(panAngle, tiltAngle);

    return { panAngle, tiltAngle };
  }

  private rotateCameras(panAngle: number, tiltAngle: number) {
    this.sphereCamera.tiltAndPan({ tilt: tiltAngle, pan: panAngle });
    this.dispatchEvent({ type: CameraEvent.CameraUpdate });
    this.updateAngularSpeeds(panAngle, tiltAngle);
  }

  endCameraDrag() {
    this.decelerateWithMomentumAnimation();

    heatMapEventManager.send(HeatMapEvents.MOVE, {
      point: new Vector2(window.innerWidth / 2, window.innerHeight / 2),
      zoomFraction: this.sphereCamera.zoomFraction,
      direction: this.heatMapMovementDirection,
    });
  }

  zoomIntoPoint(screenPoint: Vector2, zoomFactor: number) {
    const initialIntersection = this.sphereCamera.getMouseIntersection(
      screenPoint.x,
      screenPoint.y,
    );
    const oldZoomFraction = this.sphereCamera.zoomFraction;

    this.sphereCamera.zoomBy(zoomFactor);
    this.dispatchEvent({ type: CameraEvent.ControlsZoom });

    const newIntersection = this.sphereCamera.getMouseIntersection(screenPoint.x, screenPoint.y);
    if (!initialIntersection || !newIntersection) {
      this.dispatchEvent({ type: CameraEvent.CameraUpdate });
      return;
    }

    this.moveBetweenIntersections(newIntersection, initialIntersection);

    const newZoomFraction = this.sphereCamera.zoomFraction;
    heatMapEventManager.send(HeatMapEvents.ZOOM, {
      point: screenPoint,
      oldZoomFraction,
      newZoomFraction,
    });

    camera3DEventManager.send(Camera3DEvents.ZOOM, this.sphereCamera.zoom);
  }

  zoomBy(zoomFactor: number) {
    this.sphereCamera.zoomBy(zoomFactor);
    this.dispatchEvent({ type: CameraEvent.CameraUpdate });
    this.dispatchEvent({ type: CameraEvent.ControlsZoom });
    camera3DEventManager.send(Camera3DEvents.ZOOM, this.sphereCamera.zoom);
  }

  private zoomTo(fov: number) {
    this.sphereCamera.zoomTo(fov);
    this.dispatchEvent({ type: CameraEvent.CameraUpdate });
  }

  private decelerateWithMomentumAnimation() {
    if (this.speedSavedAt === undefined) return;

    this.addAnimation(
      new MomentumAnimation(arrayAverage(this.angularSpeeds[Axis.X]), this.speedSavedAt, Axis.X),
    );
    this.addAnimation(
      new MomentumAnimation(arrayAverage(this.angularSpeeds[Axis.Y]), this.speedSavedAt, Axis.Y),
    );
    this.angularSpeeds[Axis.X] = [];
    this.angularSpeeds[Axis.Y] = [];
  }

  startRotationAnimation(direction: Vector2) {
    this.rotateAnimation.addRotation(direction, this.sphereCamera.zoomFraction);
    if (this.rotateAnimation.isNotRotating()) {
      this.stopRotatitionAnimation();
    } else {
      this.addAnimation(this.rotateAnimation);
    }
    camera3DEventManager.send(Camera3DEvents.ROTATION_START, { direction });
  }

  stopRotatitionAnimation() {
    this.rotateAnimation.clearRotation();
    this.removeAnimation(this.rotateAnimation);
  }

  addAnimation(animation: CameraAnimation) {
    this.animationChain.add(animation);
  }

  removeAnimation(animation: CameraAnimation) {
    this.animationChain.delete(animation);
  }

  clearAnimations() {
    this.animationChain.clear();
  }

  private updateAngularSpeeds(panAngle: number, tiltAngle: number) {
    if (this.speedSavedAt !== undefined) {
      const timePeriod = Date.now() - this.speedSavedAt;

      this.appendAngularSpeed(Axis.X, panAngle / timePeriod);
      this.appendAngularSpeed(Axis.Y, tiltAngle / timePeriod);
    }

    this.speedSavedAt = Date.now();
  }

  private appendAngularSpeed = (axis: Axis.X | Axis.Y, newSpeedValue: number) => {
    if (!Number.isFinite(newSpeedValue) || Math.abs(newSpeedValue) > MAXIMUM_MOMENTUM_START_SPEED)
      return;

    const speedArray = this.angularSpeeds[axis];

    if (speedArray.length >= MAX_SPEED_ARRAY_LENGTH) {
      speedArray.shift();
    }
    speedArray.push(newSpeedValue);
  };

  prepareToRender() {
    this.updateAnimations();
  }

  private updateAnimations() {
    this.animationChain.forEach(animation => {
      const cameraAdjustment = animation.getAdjustment();

      if (!cameraAdjustment) {
        this.removeAnimation(animation);
        return;
      }

      if (cameraAdjustment.fov !== undefined) {
        this.zoomTo(cameraAdjustment.fov);

        if (cameraAdjustment.targetPoint) {
          const currentIntersection = this.sphereCamera.getIntersection();
          assertDefined(currentIntersection, 'Camera not looking at the sphere');
          this.moveBetweenIntersections(currentIntersection, cameraAdjustment.targetPoint);
        }
        return;
      }

      if (cameraAdjustment.pan !== undefined || cameraAdjustment.tilt !== undefined) {
        this.rotateCameras(cameraAdjustment.pan ?? 0, cameraAdjustment.tilt ?? 0);
      }
    });
  }
}
